import type { ApiPaginatedResponse, ApiResponse, ApiResult } from '@bcube/shared';
import { api } from '@/lib/api';

interface ApiErrorResponse {
  error?: string;
  message?: string;
  code?: string;
  field?: string;
  fields?: Record<string, string>;
  meta?: Record<string, string>;
}

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: ApiErrorResponse;
  };
}

export enum PriceTableStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  CANCELLED = 'CANCELLED',
  AWAITING_PUBLICATION = 'AWAITING_PUBLICATION',
  DRAFT = 'DRAFT',
  PAUSED = 'PAUSED',
}

// Brazilian UF enum
export const BRAZILIAN_UF_LIST = [
  { id: 'AC', text: 'Acre' },
  { id: 'AL', text: 'Alagoas' },
  { id: 'AP', text: 'Amapá' },
  { id: 'AM', text: 'Amazonas' },
  { id: 'BA', text: 'Bahia' },
  { id: 'CE', text: 'Ceará' },
  { id: 'DF', text: 'Distrito Federal' },
  { id: 'ES', text: 'Espírito Santo' },
  { id: 'GO', text: 'Goiás' },
  { id: 'MA', text: 'Maranhão' },
  { id: 'MT', text: 'Mato Grosso' },
  { id: 'MS', text: 'Mato Grosso do Sul' },
  { id: 'MG', text: 'Minas Gerais' },
  { id: 'PA', text: 'Pará' },
  { id: 'PB', text: 'Paraíba' },
  { id: 'PR', text: 'Paraná' },
  { id: 'PE', text: 'Pernambuco' },
  { id: 'PI', text: 'Piauí' },
  { id: 'RJ', text: 'Rio de Janeiro' },
  { id: 'RN', text: 'Rio Grande do Norte' },
  { id: 'RS', text: 'Rio Grande do Sul' },
  { id: 'RO', text: 'Rondônia' },
  { id: 'RR', text: 'Roraima' },
  { id: 'SC', text: 'Santa Catarina' },
  { id: 'SP', text: 'São Paulo' },
  { id: 'SE', text: 'Sergipe' },
  { id: 'TO', text: 'Tocantins' },
] as const;

export type BrazilianUF = (typeof BRAZILIAN_UF_LIST)[number]['id'];

// Billing options
export const TO_BILL_FOR_OPTIONS = [
  { id: '20.618.574/0002-16', text: 'BWS - 20.618.574/0002-16' },
  { id: '41.459.104/0001-46', text: 'HYBRID - 41.459.104/0001-46 (MATRIZ)' },
  { id: '41.459.104/0002-27', text: 'HYBRID - 41.459.104/0002-27 (FILIAL MG)' },
  { id: '34.984.723/0001-94', text: 'ICB - 34.984.723/0001-94 (MATRIZ)' },
  { id: '34.984.723/0002-75', text: 'ICB - 34.984.723/0002-75 (FILIAL SP)' },
  { id: '34.984.723/0003-56', text: 'ICB 34.984.723/0003-56 (FILIAL MG)' },
  { id: '31.941.680/0001-71', text: 'MGC - 31.941.680/0001-71' },
  { id: '14.334.132/0001-64', text: 'WFC - 14.334.132/0001-64' },
] as const;

// Price range interface
export interface IPriceRange {
  from: number;
  to: number | null;
  unitPriceInCents: number;
}

// Equipment payment interface
export interface IEquipmentPayment {
  type: 'batch' | 'unit';
  paymentType: 'credit' | 'upfront';
  productId: string;
  productName: string;
  unitPriceInCents: number;
  priceRange: IPriceRange[];
}

// Simcard payment interface
export interface ISimcardPayment {
  carriers: string[];
  dataAmountMb: number;
  planType: string;
  provider: string;
  priceWithoutDeviceInCents: number;
  priceInBundleInCents: number;
}

// Service payment interface
export interface IServicePayment {
  serviceId: string;
  monthlyPriceInCents?: number;
  yearlyPriceInCents?: number;
  fixedPriceInCents?: number;
}

// Price table condition interface
export interface IPriceTableCondition {
  id: string;
  salesFor: BrazilianUF[];
  billingLimitInCents: number;
  toBillFor: string;
}

// Condition group interface
export interface IPriceTableConditionGroup {
  id: string;
  priority?: boolean;
  conditions: IPriceTableCondition[];
}

export interface PriceTable {
  id: string;
  seq: number;
  name: string;
  startDateTime: string;
  endDateTime?: string;
  isTemporary: boolean;
  status: PriceTableStatus;
  created_at: string;
  updated_at?: string;
  activated_at?: string;
  inactivated_at?: string;
  groups?: IPriceTableConditionGroup[];
  equipmentPayment?: IEquipmentPayment[];
  equipmentSimcardPayment?: IEquipmentPayment[];
  simcardPayment?: ISimcardPayment[];
  servicePayment?: IServicePayment[];
}

export type PriceTableHistoryAction =
  | 'CREATED'
  | 'UPDATED'
  | 'PUBLISHED'
  | 'ACTIVATED'
  | 'PAUSED'
  | 'RESUMED'
  | 'CANCELLED'
  | 'INACTIVATED'
  | 'CLONED';

export interface PriceTableHistoryChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface PriceTableHistoryEntry {
  id: string;
  priceTableId: string;
  userId: string;
  userName: string;
  action: PriceTableHistoryAction;
  changes?: PriceTableHistoryChange[];
  created_at: string;
}

export interface PriceTableHistoryResponse {
  data: PriceTableHistoryEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface PriceTableHistoryParams {
  page?: number;
  limit?: number;
}

export interface PriceTableQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: PriceTableStatus;
  isTemporary?: boolean;
  useStatusOrder?: boolean;
  createdAt?: string;
  startDate?: string;
  startDateFrom?: string;
  startDateTo?: string;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  pages: number;
  limit: number;
}

// Create/Update DTOs
export interface CreatePriceTableDto {
  name: string;
  startDateTime: Date | string;
  endDateTime?: Date | string;
  isTemporary: boolean;
  groups: IPriceTableConditionGroup[];
  equipmentPayment?: IEquipmentPayment[];
  equipmentSimcardPayment?: IEquipmentPayment[];
  simcardPayment?: ISimcardPayment[];
  servicePayment?: IServicePayment[];
}

export interface UpdatePriceTableDto extends Partial<CreatePriceTableDto> {
  id: string;
}

// API response types

export const priceTableApi = {
  findMany: async (params?: PriceTableQueryParams): Promise<PaginatedResponse<PriceTable>> => {
    const queryParams: Record<string, string | number | boolean> = {};

    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.search) queryParams.search = params.search;
    if (params?.status) queryParams.status = params.status;
    if (params?.useStatusOrder) queryParams.useStatusOrder = params.useStatusOrder;
    if (params?.isTemporary !== undefined) queryParams.isTemporary = params.isTemporary;
    if (params?.createdAt) queryParams.createdAt = params.createdAt;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.startDateFrom) queryParams.startDateFrom = params.startDateFrom;
    if (params?.startDateTo) queryParams.startDateTo = params.startDateTo;

    const { data } = await api.get<ApiPaginatedResponse<PriceTable>>('/commercial/price-tables', {
      params: queryParams,
    });
    return {
      docs: data.data,
      total: data.meta.total,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<PriceTable> => {
    const { data } = await api.get<ApiResponse<PriceTable>>(`/commercial/price-tables/${id}`);
    return data.data;
  },

  create: async (payload: CreatePriceTableDto): Promise<ApiResult<PriceTable>> => {
    try {
      const { data } = await api.post<ApiResponse<PriceTable>>('/commercial/price-tables', payload);
      return { success: true, data: data.data };
    } catch (error: unknown) {
      const axiosError = error as AxiosLikeError;
      const responseData = axiosError?.response?.data;
      const status = axiosError?.response?.status;
      const fields: Record<string, string> = { ...responseData?.fields };

      if (status === 409 && responseData?.field) {
        fields[responseData.field] = responseData.message || 'Já existe uma tabela com este nome';
      }

      return {
        success: false,
        error: {
          global: responseData?.message || 'Erro ao criar tabela de preços',
          fields: Object.keys(fields).length > 0 ? fields : undefined,
        },
      };
    }
  },

  update: async (
    id: string,
    payload: Partial<CreatePriceTableDto>,
  ): Promise<ApiResult<PriceTable>> => {
    try {
      const { data } = await api.patch<ApiResponse<PriceTable>>(
        `/commercial/price-tables/${id}`,
        payload,
      );
      return { success: true, data: data.data };
    } catch (error: unknown) {
      const axiosError = error as AxiosLikeError;
      const responseData = axiosError?.response?.data;
      const status = axiosError?.response?.status;
      const fields: Record<string, string> = { ...responseData?.fields };

      if (status === 409 && responseData?.field) {
        fields[responseData.field] = responseData.message || 'Já existe uma tabela com este nome';
      }

      return {
        success: false,
        error: {
          global: responseData?.message || 'Erro ao atualizar tabela de preços',
          fields: Object.keys(fields).length > 0 ? fields : undefined,
        },
      };
    }
  },

  clone: async (id: string): Promise<{ success: boolean; id: string }> => {
    const { data } = await api.post<ApiResponse<{ id: string }>>(
      `/commercial/price-tables/${id}/clone`,
    );
    return { success: true, id: data.data.id };
  },

  publish: async (id: string): Promise<ApiResult> => {
    try {
      await api.post<ApiResponse<null>>(`/commercial/price-tables/${id}/publish`);
      return { success: true };
    } catch (error: unknown) {
      const responseData = (error as AxiosLikeError)?.response?.data;
      return {
        success: false,
        error: {
          global:
            responseData?.error || responseData?.message || 'Erro ao publicar tabela de preços',
          code: responseData?.code,
          field: responseData?.field,
          meta: responseData?.meta,
        },
      };
    }
  },

  cancel: async (id: string): Promise<ApiResult> => {
    try {
      await api.post<ApiResponse<null>>(`/commercial/price-tables/${id}/cancel`);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global:
            (error as AxiosLikeError)?.response?.data?.message ||
            'Erro ao cancelar tabela de preços',
        },
      };
    }
  },

  getHistory: async (
    id: string,
    params?: PriceTableHistoryParams,
  ): Promise<PriceTableHistoryResponse> => {
    const queryParams: Record<string, number> = {};
    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;

    const { data } = await api.get<ApiResponse<PriceTableHistoryResponse>>(
      `/commercial/price-tables/${id}/history`,
      { params: queryParams },
    );
    return data.data;
  },

  inactivate: async (id: string): Promise<ApiResult> => {
    try {
      await api.post<ApiResponse<null>>(`/commercial/price-tables/${id}/inactivate`);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: {
          global:
            (error as AxiosLikeError)?.response?.data?.message ||
            'Erro ao inativar tabela de preços',
        },
      };
    }
  },
};
